/**
 * Page 2 of Create trigger drawer
 * Configure job
 * Select job artifact, alias/version, config, target project and queue
 */
// @todo: Migrate to 'Dialog.Popover' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popover} from '@material-ui/core';
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useNodeValue} from '@wandb/weave/react';
import React, {useEffect, useMemo, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {useShallow} from 'zustand/react/shallow';

import {
  DefaultResourceConfig,
  useAllProjectNamesQuery,
  useEntityDefaultResourceConfigsQuery,
} from '../../../../generated/graphql';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../util/artifacts';
import {trackLaunchConfigUpdated} from '../../../../util/navigation';
import {SemanticLoader as Loader} from '../../../utility/InstrumentedLoader';
import {useEntityQueueOptions, useProjectView} from '../../hooks';
import {getJobDefinitionFileOp, JobFile} from '../../JobDetail/utilsJobFile';
import {JobRun} from '../../JobDetail/utilTypes';
import {JobDrawerOverrides} from '../../JobDrawer/JobDrawerOverrides';
import {SelectRunPanel} from '../../JobDrawer/SelectRunPanel';
import {SelectJobVersion} from '../../SelectJobVersion';
import {getDefaultQueue, QueueOption, SelectQueue} from '../../SelectQueue';
import {extractConfig} from '../../utils';
import {JsonObject} from '../../utilTypes';
import {ConfigKey} from '../ConfigKey';
import {OptionType} from '../TriggersTable/utilTypes';
import {isValidQueue, useActionConfigStore} from './ActionStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {getTemplateStrings, useEventStore} from './EventStore';
import {SelectJob} from './SelectJob';
import {JobOption} from './types';

type JobConfigurationProps = {
  onClose: () => void;
};

const useQueues = (
  entityName: string | undefined,
  projectName: string | undefined
) => {
  const queryResourceConfigs = useEntityDefaultResourceConfigsQuery({
    variables: {entityName: entityName ?? ''},
    skip: entityName == null,
  });
  const defaultResourceConfigs = useMemo(() => {
    const mapIdToResource: Record<string, DefaultResourceConfig> = {};
    const edges =
      queryResourceConfigs.data?.entity?.defaultResourceConfigs?.edges ?? [];
    for (const edge of edges) {
      mapIdToResource[edge.node.id] = edge.node;
    }
    return mapIdToResource;
  }, [queryResourceConfigs]);
  const queryQueues = useEntityQueueOptions(
    entityName,
    projectName,
    defaultResourceConfigs,
    true
  );

  const queues = queryQueues.queues;
  return {
    queues,
    loading: queryQueues.loading,
    error: queryQueues.error,
    defaultQueue: getDefaultQueue(queues),
  };
};

export const JobConfiguration: React.FC<JobConfigurationProps> = ({
  onClose,
}) => {
  const eventConfig = useEventStore(useShallow(s => s));
  const templateStrings = getTemplateStrings(eventConfig.eventType);
  const {updateJobConfig, entityName} = useActionConfigStore(
    useShallow(s => s)
  );
  const {
    targetProjectName,
    sourceProjectName,
    queuedJob,
    queuedJobAlias,
    queuedJobID,
    overrides,
    overridesError,
    selectedQueue,
  } = useActionConfigStore(useShallow(s => s.job));
  const onSelectJob = (jobOption: JobOption | null) => {
    const job = jobOption?.value ?? null;
    updateJobConfig({
      queuedJobID: jobOption,
      queuedJob: job,
      queuedJobAlias: job?.versionInfo?.runTarget ?? null,
    });
  };
  const previousRuns = queuedJob?.runs.filter(
    run => run.version === queuedJob?.versionInfo?.runTargetNum
  );
  const [cloneRun, setCloneRun] = useState<JobRun | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const onPopoverClose = () => {
    setAnchorEl(null);
  };

  // Overrides
  // Get overrides template
  const contents = getJobDefinitionFileOp({
    entityName: entityName ?? '',
    projectName: queuedJob?.projectName ?? '',
    jobName: queuedJob?.name ?? '',
    alias: queuedJob?.versionInfo.runTarget ?? '',
  });
  const {result: jobDefinitionStr, loading: jobFileLoading} =
    useNodeValue(contents);

  const onOverridesChange = (value: string) => {
    updateJobConfig({overrides: value, overridesError: ''});
  };

  // Set config based on overrides, if any
  const setConfig = (config: JsonObject) => {
    const newOverrides = {
      args: [],
      run_config: config,
      entry_point: [],
    };
    const overrideStr = JSON.stringify(newOverrides, null, 4);
    onOverridesChange(overrideStr);
  };

  // Set config based on run selected
  const onSelectRun = (run: JobRun | null) => {
    setCloneRun(run);
    if (!run) {
      return;
    }
    const config = extractConfig(run.config);
    setConfig(config);
  };

  useEffect(() => {
    if (!jobDefinitionStr) {
      return;
    }
    const jobFile = new JobFile(jobDefinitionStr);
    setConfig(jobFile.getConfigInputExample());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDefinitionStr]);

  useEffect(() => {
    try {
      JSON.parse(overrides);
      updateJobConfig({overridesError: ''});
    } catch (err) {
      updateJobConfig({overridesError: 'Overrides must be valid JSON'});
    }
  }, [overrides, updateJobConfig]);

  const onSelectQueue = (option: QueueOption) => {
    if (option !== selectedQueue) {
      // Only track the event if the selected queue was changed
      trackLaunchConfigUpdated({
        eventType: 'Launch config updated via Create Trigger modal',
        configType: 'queue_name',
      });
    }
    updateJobConfig({selectedQueue: option});
  };

  // we use the target project name here because it's the project where the job will be run,
  // so we need the queues for that project.
  const {
    queues,
    defaultQueue,
    error: queueError,
    loading: queueLoading,
  } = useQueues(entityName, targetProjectName);

  // update the selected queue to be the default one if it's not already set
  // This is needed so that the default option shown in the Queue dropdown
  // is set as the jobConfig selectedQueue option if the user hasn't changed it.
  useEffect(() => {
    if (!isValidQueue(selectedQueue)) {
      updateJobConfig({selectedQueue: defaultQueue});
    }
  }, [defaultQueue, selectedQueue, updateJobConfig]);

  // we use the source project name here because it's the project where we are selecting runs
  // from, so we want to show the colors for that project.
  const queryColors = useProjectView(entityName, sourceProjectName);

  const {
    projectOptions,
    error: projectError,
    loading: projectLoading,
  } = useAllProjects(entityName);

  if (queueLoading || projectLoading) {
    return <Loader active name="create-trigger-drawer-projects" />;
  }

  if (queueError || projectError) {
    // In the future, it might be nice to not completely error out when
    // there's an error while querying
    onClose();
    toast.error('Error fetching information');
    return null;
  }

  return (
    <Tailwind>
      <div className="mb-4 mt-16 flex items-center"></div>
      <S.Description>
        Select and configure a job from your existing projects. Use the
        suggested keys to reference condition objects in the job parameters.
      </S.Description>
      <div className="mb-4 mt-16 flex items-center">
        <div className="text-[1rem] font-semibold leading-[2rem]">
          Available projects
        </div>
      </div>
      <Select<OptionType | null>
        options={projectOptions}
        defaultValue={
          sourceProjectName
            ? {
                label: sourceProjectName,
                value: sourceProjectName,
              }
            : null
        }
        placeholder="Select project"
        onChange={(option: OptionType | null) => {
          if (option) {
            updateJobConfig({sourceProjectName: option.value});
            onSelectJob(null);
          }
        }}
      />
      {sourceProjectName != null && entityName != null && (
        <>
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Available jobs
            </div>
          </div>
          <SelectJob
            entityName={entityName}
            projectName={sourceProjectName}
            selectedJobId={queuedJobID}
            onSelectJob={onSelectJob}
          />
        </>
      )}
      {!contents && jobFileLoading && (
        <Loader active name="drawer-job-config-loader" />
      )}
      {queuedJob && (
        <>
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Job version
            </div>
          </div>
          <SelectJobVersion
            jobVersions={queuedJob.versions}
            selectedAlias={queuedJobAlias ?? ''}
            onSelectJobAlias={s => updateJobConfig({queuedJobAlias: s})}
          />
          {contents && !jobFileLoading && (
            <>
              {previousRuns && (
                <>
                  <div className="mb-4 mt-16 flex items-center">
                    <div className="text-[1rem] font-semibold leading-[2rem]">
                      Clone from
                    </div>
                  </div>
                  <Select
                    id="clone-from-select"
                    inputValue={cloneRun?.displayName}
                    onMenuOpen={() => {
                      setAnchorEl(document.getElementById('clone-from-select'));
                    }}
                    menuIsOpen={false}
                  />
                  <Popover
                    id="job-drawer-paste-from-popover"
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={onPopoverClose}
                    // TODO: Can this be translated to tailwind?
                    PaperProps={{
                      style: {
                        marginTop: '8px',
                        width:
                          document.getElementById('clone-from-select')
                            ?.offsetWidth ?? 0,
                      },
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}>
                    <SelectRunPanel
                      runs={previousRuns}
                      customRunColors={queryColors?.customRunColors}
                      onSelectRun={onSelectRun}
                      onClose={onPopoverClose}
                    />
                  </Popover>
                </>
              )}
              <div className="mb-4 mt-16 flex items-center">
                <div className="text-[1rem] font-semibold leading-[2rem]">
                  Job overrides
                </div>
              </div>
              <JobDrawerOverrides
                overrides={overrides}
                onOverridesChange={onOverridesChange}
                onSelectRun={onSelectRun}
              />
              {overridesError && <S.Error>{overridesError}</S.Error>}
              <S.PlainText>
                Use the following keys to refer to condition objects in the job
                parameters:
              </S.PlainText>
              {templateStrings.map(ts => (
                <S.TemplateHelperRow key={ts.value}>
                  <S.TemplateHelperKey key={`key-${ts.value}`}>
                    <ConfigKey configKey={ts.value} />
                  </S.TemplateHelperKey>
                  <S.TemplateHelperString key={ts.value}>
                    {ts.help}
                  </S.TemplateHelperString>
                </S.TemplateHelperRow>
              ))}
            </>
          )}
        </>
      )}
      {queuedJobAlias && (
        <>
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Destination project
            </div>
          </div>
          <Select<OptionType | null>
            options={projectOptions}
            value={
              targetProjectName
                ? {
                    label: targetProjectName,
                    value: targetProjectName,
                  }
                : null
            }
            placeholder="Select project"
            onChange={(option: OptionType | null) => {
              if (option) {
                updateJobConfig({targetProjectName: option.value});
                trackLaunchConfigUpdated({
                  eventType: 'Launch config updated via Create Trigger modal',
                  configType: 'project_name',
                });
              }
            }}
          />
          {/*TODO: Add option for resource configuration*/}
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Queue
            </div>
          </div>
          <SelectQueue
            options={queues}
            defaultQueueOption={defaultQueue ?? undefined}
            onSelectQueue={onSelectQueue}
          />
        </>
      )}
    </Tailwind>
  );
};

const useAllProjects = (entityName: string | undefined) => {
  const queryAllProjects = useAllProjectNamesQuery({
    variables: {
      entityName: entityName ?? '',
      first: 1000,
      order: '-createdAt',
    },
    fetchPolicy: 'cache-and-network',
    skip: entityName == null,
  });

  const allProjects =
    queryAllProjects.data?.projects?.edges
      .map(p => p.node?.name ?? '')
      .filter(p => p !== '' && p !== OLD_MODEL_REGISTRY_PROJECT_NAME) ?? [];

  // Launch configuration (target project and queue)
  const projectOptions: OptionType[] = allProjects?.map(name => {
    return {
      label: name,
      value: name,
    };
  });
  return {
    projectOptions,
    error: queryAllProjects.error,
    loading: queryAllProjects.loading,
  };
};
